import i18next from 'i18next';
import {Observable} from 'rxjs';
import {change} from 'redux-form';
import {List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import CalendarApi from 'web-app/api/calendar-api';
import FileRecord from 'web-app/react/components/file-upload/file-record';
import * as FileActions from 'web-app/react/components/file-upload/actions';
import InstitutionBehaviors from 'web-app/behaviors/institution-behavior-ids';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {track} from 'web-app/util/famlytics';

import * as Actions from './actions';
import * as Constants from './constants';

const eventObservable = eventId =>
    Observable.fromPromise(CalendarApi.showEvent(eventId)).map(({event}) => ({
        event,
        files: event.image
            ? List([
                  new FileRecord({
                      name: '',
                      type: 'image',
                      id: event.imageId,
                      uploadProgress: null,
                      previewUrl: event.image.large,
                      thumbnailUrl: event.image.large,
                      videoDuration: null,
                  }),
              ])
            : List(),
    }));

const initialFetch = action$ =>
    action$.ofType(Actions.initialFetch.type).mergeMap(({payload}) => {
        const getEventObs = payload.eventId
            ? eventObservable(payload.eventId)
            : Observable.of({
                  files: List(),
              });
        const getListObs = Observable.fromPromise(CalendarApi.getCalendarLists()).map(({institutions}) => {
            return institutions.filter(inst => {
                return inst.behaviors.some(
                    behavior => behavior.id === InstitutionBehaviors.CanCreateEventInInstitution,
                );
            });
        });
        return Observable.zip(getEventObs, getListObs)
            .mergeMap(([{event, files}, institutions]) => {
                const institutionId = event ? event.institutionId : institutions[0].institutionId;
                return Observable.fromPromise(CalendarApi.getPossibleRecipients(institutionId)).flatMap(response => {
                    const recipients = response.map(recipient => ({
                        value: recipient.id,
                        label: recipient.name,
                        subtitle: recipient.group,
                        image: recipient.image,
                    }));

                    return [
                        Actions.initialFetchSuccess.action({
                            event,
                            institutions,
                            recipients,
                        }),
                        FileActions.loadFiles(Constants.formName, files),
                    ];
                });
            })
            .catch(e => Observable.of(Actions.initialFetchFailed.action(e)));
    });

const updateRecipients = action$ =>
    action$.ofType(Actions.updateRecipients.type).mergeMap(({payload}) => {
        return Observable.fromPromise(CalendarApi.getPossibleRecipients(payload.institutionId))
            .flatMap(response => {
                const recipients = response.map(recipient => ({
                    value: recipient.id,
                    label: recipient.name,
                    subtitle: recipient.group,
                    image: recipient.image,
                }));

                return [
                    Actions.updateRecipientsSuccess.action({
                        recipients,
                    }),
                    change(Constants.formName, 'selectedRecipients', ''),
                ];
            })
            .catch(e => Observable.of(Actions.updateRecipientsFailed.action(e)));
    });

const createEvent = action$ =>
    action$.ofType(Actions.createEvent.type).mergeMap(({payload}) => {
        const {
            institutionId,
            title,
            description,
            startTime,
            endTime,
            selectedRecipients,
            rsvpRequired,
            deadline,
            postToFeed,
            imageId,
        } = payload.event;

        return Observable.fromPromise(
            CalendarApi.create(
                institutionId,
                title,
                description,
                startTime,
                endTime,
                selectedRecipients.split(','),
                rsvpRequired,
                deadline,
                imageId,
                postToFeed,
            ),
        )
            .flatMap(() => {
                track(TrackingEvents.CALENDAR_EVENT_CREATED);
                return [
                    Actions.createEventSuccess.action(),
                    GlobalEventActions.updateSuccess.action(i18next.t('calendar.event.successfullyCreated')),
                ];
            })
            .catch(e => Observable.of(Actions.createEventFailed.action(e)));
    });

const updateEvent = action$ =>
    action$.ofType(Actions.updateEvent.type).mergeMap(({payload}) => {
        const {
            eventId,
            institutionId,
            title,
            description,
            startTime,
            endTime,
            selectedRecipients,
            rsvpRequired,
            deadline,
            postToFeed,
            imageId,
        } = payload.event;

        return Observable.fromPromise(
            CalendarApi.updateEvent(
                institutionId,
                eventId,
                title,
                description,
                startTime,
                endTime,
                selectedRecipients.split(','),
                rsvpRequired,
                deadline,
                imageId,
                postToFeed,
            ),
        )
            .flatMap(() => [
                Actions.updateEventSuccess.action(),
                GlobalEventActions.updateSuccess.action(i18next.t('calendar.event.successfullyUpdated')),
            ])
            .catch(e => Observable.of(Actions.updateEventFailed.action(e)));
    });

export default combineEpics(initialFetch, updateRecipients, createEvent, updateEvent);
